// Всплывающее меню (components/Dropdown/README.md) на Radix DropdownMenu: открывается под кнопкой
// и переворачивается вверх, Esc и клик снаружи закрывают, фокус возвращается на кнопку.
// Загрузка, пустой список и сбой с кодом — строкой-сообщением DropdownMessage, а не неактивным
// пунктом: неактивный пункт (opacity) читается хуже 4.5:1 и значит «действие недоступно».
// «Повторить» под сбоем уходит из меню вместе со сбоем, а фокус остаётся в меню (T169).
// Пункт «Свойство…» (DropdownChoiceItem) превращает это же меню в выбор значения, без нового
// окна; Esc возвращает к меню (ux.md, раздел 5; 005-2, T012).
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, CircleAlert, RotateCcw } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';
import { useDelayedFlag } from '../lib/use-delayed.ts';
import { ShortcutKeys } from './Kbd.tsx';
import type { Shortcut } from './Kbd.tsx';

export interface DropdownProps {
  /** Кнопка, которая открывает меню; должна принимать ref и обработчики. */
  trigger: ReactElement;
  children: ReactNode;
  /** Подпись меню для программ чтения экрана. */
  label?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * После закрытия: preventDefault оставляет фокус там, куда его перевёл обработчик, — например,
   * на строке таблицы, если меню открыли правым кликом или Shift+F10 (README «Доступность»).
   */
  onCloseAutoFocus?: (event: Event) => void;
  className?: string;
}

/**
 * Сообщение меню: id — для aria-describedby меню, если сообщение на виду (shown); busy — идёт
 * загрузка (aria-busy).
 */
interface MenuNote {
  id: string;
  shown: boolean;
  busy: boolean;
}

/** Сообщения меню сообщают о себе меню: им оно описано, на время загрузки — занято. */
const MenuNotesContext = createContext<((note: MenuNote) => () => void) | null>(null);

/**
 * Пункт с фокусом уходит из меню («Повторить» после нажатия): меню возвращает фокус в себя.
 * keyboard — пункт нажали с клавиатуры: фокус — на соседний пункт (near), иначе на первый, когда
 * он есть или появится.
 */
interface FocusRequest {
  keyboard: boolean;
  near: HTMLElement | null;
}
const MenuFocusContext = createContext<((request: FocusRequest) => void) | null>(null);

/** Пункты меню, на которые можно перевести фокус. */
const MENU_ITEMS = ['menuitem', 'menuitemradio', 'menuitemcheckbox']
  .map((role) => `[role="${role}"]:not([data-disabled])`)
  .join(', ');

/**
 * Фокус после ухода пункта (T169; README «Доступность»: Esc и Tab работают в меню, фокус
 * не теряется). Меню без модального режима само фокус не держит: без этого он упал бы на страницу,
 * а открытое меню перестало бы слушать стрелки и Esc. Как при открытии меню у Radix: с клавиатуры —
 * пункт (соседний с ушедшим или первый; нет пунктов — само меню, а первый пункт — когда появится),
 * мышью — само меню.
 */
function useKeepFocus() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [request, setRequest] = useState<FocusRequest | null>(null);
  const awaitingItem = useRef(false);
  const keepFocus = useCallback((next: FocusRequest) => setRequest(next), []);

  // Пункт уже удалён, его сбор у Radix — тоже (эффект после перерисовки по запросу).
  useEffect(() => {
    if (!request) return;
    const content = contentRef.current;
    if (!content || content.dataset.state !== 'open') return;
    const active = document.activeElement;
    // Фокус уже перевели — в меню или туда, куда увёл обработчик пункта, — его не перебиваем.
    if (active !== null && active !== document.body) return;
    const { keyboard, near } = request;
    const item = !keyboard
      ? null
      : near !== null && content.contains(near) && near.matches(MENU_ITEMS)
        ? near
        : content.querySelector<HTMLElement>(MENU_ITEMS);
    (item ?? content).focus({ preventScroll: true });
    awaitingItem.current = keyboard && item === null;
  }, [request]);

  // Пункты появились (ответ на повтор), фокус всё ещё на самом меню — первый пункт.
  useEffect(() => {
    if (!awaitingItem.current) return;
    const content = contentRef.current;
    if (!content || document.activeElement !== content) {
      awaitingItem.current = false;
      return;
    }
    const first = content.querySelector<HTMLElement>(MENU_ITEMS);
    if (!first) return;
    awaitingItem.current = false;
    first.focus({ preventScroll: true });
  });

  return { contentRef, keepFocus };
}

/** Выбор, в который превратилось меню: подпись, текущее значение и пункты выбора. */
interface MenuChoice {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: ReactNode;
  /** Номер пункта «Свойство…» среди пунктов меню: на него вернётся фокус после Esc. */
  opener: number;
}

const MenuChoiceContext = createContext<((choice: MenuChoice) => void) | null>(null);

/**
 * Содержимое открытого меню: пункты или выбор, в который превратил меню DropdownChoiceItem.
 * Состояние живёт, пока меню открыто: закрытое меню откроется снова с пунктами. Фокус после
 * смены содержимого — на текущем значении выбора (ux.md, раздел 5), после Esc — на пункте
 * «Свойство…», с которого выбор открыли.
 */
function MenuBody({
  contentRef,
  backRef,
  children,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
  /** Esc в выборе: вернуть меню; true — Esc обработан и меню не закрывается. */
  backRef: RefObject<(() => boolean) | null>;
  children: ReactNode;
}) {
  const [choice, setChoice] = useState<MenuChoice | null>(null);
  const focusTo = useRef<'choice' | number | null>(null);
  const openChoice = useCallback((next: MenuChoice) => {
    focusTo.current = 'choice';
    setChoice(next);
  }, []);

  useEffect(() => {
    if (!choice) return undefined;
    backRef.current = () => {
      focusTo.current = choice.opener;
      setChoice(null);
      return true;
    };
    return () => {
      backRef.current = null;
    };
  }, [choice, backRef]);

  // Пункт с фокусом ушёл вместе со сменой содержимого: фокус — в новое содержимое меню.
  useLayoutEffect(() => {
    const target = focusTo.current;
    if (target === null) return;
    focusTo.current = null;
    const content = contentRef.current;
    if (!content) return;
    const items = Array.from(content.querySelectorAll<HTMLElement>(MENU_ITEMS));
    const item =
      target === 'choice'
        ? (items.find((element) => element.getAttribute('aria-checked') === 'true') ?? items[0])
        : (items[target] ?? items[0]);
    (item ?? content).focus({ preventScroll: true });
  }, [choice, contentRef]);

  return (
    <MenuChoiceContext.Provider value={openChoice}>
      {choice ? (
        <>
          <Menu.Label className="t-mh">{choice.label}</Menu.Label>
          <Menu.RadioGroup
            aria-label={choice.label}
            value={choice.value}
            onValueChange={(next) => {
              // Текущее значение выбрано снова — меню просто закрывается.
              if (next !== choice.value) choice.onValueChange(next);
            }}
          >
            {choice.options}
          </Menu.RadioGroup>
        </>
      ) : (
        children
      )}
    </MenuChoiceContext.Provider>
  );
}

export function Dropdown({
  trigger,
  children,
  label,
  align = 'start',
  side = 'bottom',
  open,
  onOpenChange,
  onCloseAutoFocus,
  className,
}: DropdownProps) {
  const [notes, setNotes] = useState<readonly MenuNote[]>([]);
  const addNote = useCallback((note: MenuNote) => {
    setNotes((current) => [...current.filter((item) => item.id !== note.id), note]);
    return () => setNotes((current) => current.filter((item) => item.id !== note.id));
  }, []);
  const describedBy = notes
    .filter((note) => note.shown)
    .map((note) => note.id)
    .join(' ');
  const { contentRef, keepFocus } = useKeepFocus();
  const choiceBack = useRef<(() => boolean) | null>(null);
  return (
    // Без модального режима: страница не скрывается от программ чтения (aria-hidden) и не
    // блокируется; клик снаружи, Esc и Tab закрывают меню, фокус возвращается на кнопку.
    <Menu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          ref={contentRef}
          aria-label={label}
          // Своя подпись меню важнее имени кнопки (Radix подписывает меню кнопкой); без неё
          // остаётся подпись по кнопке.
          {...(label ? { 'aria-labelledby': undefined } : {})}
          // Сообщение меню слышно при открытии; пока идёт загрузка, меню занято.
          aria-describedby={describedBy || undefined}
          aria-busy={notes.some((note) => note.busy) || undefined}
          align={align}
          side={side}
          sideOffset={4}
          collisionPadding={8}
          loop
          // Esc в выборе DropdownChoiceItem возвращает к меню, а не закрывает его.
          onEscapeKeyDown={(event) => {
            if (choiceBack.current?.()) event.preventDefault();
          }}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cx('t-pop kp-pop', className)}
        >
          <MenuFocusContext.Provider value={keepFocus}>
            <MenuNotesContext.Provider value={addNote}>
              <MenuBody contentRef={contentRef} backRef={choiceBack}>
                {children}
              </MenuBody>
            </MenuNotesContext.Provider>
          </MenuFocusContext.Provider>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

export interface DropdownItemProps {
  children: ReactNode;
  icon?: LucideIcon;
  /** Клавиша справа серым: «?» или «G затем H». */
  shortcut?: Shortcut;
  /** Опасный пункт: последний, красный, за разделителем. */
  danger?: boolean;
  disabled?: boolean;
  onSelect?: (event: Event) => void;
}

export function DropdownItem({
  children,
  icon,
  shortcut,
  danger,
  disabled,
  onSelect,
}: DropdownItemProps) {
  return (
    <Menu.Item className={cx('t-mi', danger && 'danger')} disabled={disabled} onSelect={onSelect}>
      {icon ? <Icon icon={icon} className="t-ico" /> : null}
      {children}
      {shortcut ? (
        <span className="r">
          <ShortcutKeys shortcut={shortcut} />
        </span>
      ) : null}
    </Menu.Item>
  );
}

export interface DropdownChoiceItemProps<V extends string> {
  /** Свойство: «Вид». Пункт меню — «Вид…», заголовок выбора — «Вид». */
  label: string;
  /** Текущее значение — отмечено галочкой. */
  value: V;
  /** Выбрано другое значение; меню закрывается. */
  onValueChange: (value: V) => void;
  /** Значения выбора — DropdownRadioItem, те же, что у пилюли свойства (ux.md, раздел 5). */
  children: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
}

/**
 * Пункт контекстного меню «Свойство…»: превращает это же меню в выбор значения, без нового окна
 * (ux.md, раздел 5). В выборе фокус — на текущем значении, ↑ ↓ Home End — по значениям, Enter
 * выбирает и закрывает меню, Esc — назад к пунктам меню, на этот пункт.
 */
export function DropdownChoiceItem<V extends string>({
  label,
  value,
  onValueChange,
  children,
  icon,
  disabled,
}: DropdownChoiceItemProps<V>) {
  const openChoice = useContext(MenuChoiceContext);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <Menu.Item
      ref={ref}
      className="t-mi"
      disabled={disabled}
      onSelect={(event) => {
        if (!openChoice) return;
        // Меню не закрывается: его содержимое сменится выбором.
        event.preventDefault();
        const item = ref.current;
        const items = Array.from(
          item?.closest('[role="menu"]')?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [],
        );
        openChoice({
          label,
          value,
          onValueChange: (next) => onValueChange(next as V),
          options: children,
          opener: item ? Math.max(0, items.indexOf(item)) : 0,
        });
      }}
    >
      {icon ? <Icon icon={icon} className="t-ico" /> : null}
      {`${label}…`}
    </Menu.Item>
  );
}

export type DropdownMessageTone = 'note' | 'loading' | 'error';

export interface DropdownMessageProps {
  /** Что здесь происходит: «Загружаем роли…», «Авторов пока нет», «Не удалось загрузить авторов». */
  children: ReactNode;
  /**
   * note — пояснение (пустой список); loading — загрузка: текст появляется, только если ожидание
   * дольше 0,3 с (ux.md, раздел 14), меню на это время занято (aria-busy); error — сбой: значок
   * и цвет color-bad.
   */
  tone?: DropdownMessageTone;
  /** Код ошибки после текста моноширинным шрифтом: «. Код ошибки SYS-001». */
  code?: string;
  /** Путь вперёд после сбоя: пункт «Повторить» под сообщением; меню остаётся открытым. */
  onRetry?: () => void;
}

/**
 * Строка-сообщение в меню: не пункт — не выбирается, не подсвечивается, стрелки её пропускают;
 * текст читаем (color-ink-2, у сбоя — color-bad; README «Доступность»). Меню описано ею
 * (aria-describedby): программа чтения произносит её при открытии меню. В меню ДОЛЖЕН остаться
 * хотя бы один пункт (role="menu" без пунктов — нарушение ARIA), кроме загрузки: у сбоя без
 * других пунктов передайте onRetry.
 */
export function DropdownMessage({ children, tone = 'note', code, onRetry }: DropdownMessageProps) {
  const id = useId();
  const addNote = useContext(MenuNotesContext);
  const loading = tone === 'loading';
  const late = useDelayedFlag(loading);
  const shown = !loading || late;
  useLayoutEffect(() => {
    if (!addNote) return undefined;
    // Пока сообщения загрузки не видно, меню занято, но не описано: ссылки на пустое место нет.
    return addNote({ id, shown, busy: loading });
  }, [addNote, id, shown, loading]);
  return (
    <>
      {shown ? (
        <div id={id} className={cx('kp-mi-note', tone === 'error' && 'is-error')}>
          {tone === 'error' ? <Icon icon={CircleAlert} className="t-ico" /> : null}
          <span>
            {children}
            {code != null ? (
              <>
                . Код ошибки <span className="kp-code">{code}</span>
              </>
            ) : null}
          </span>
        </div>
      ) : null}
      {onRetry ? <RetryItem onRetry={onRetry} /> : null}
    </>
  );
}

/**
 * «Повторить» под сбоем: меню остаётся открытым. Повтор пошёл — сбой сменяется загрузкой, и пункт
 * уходит из меню вместе с фокусом; фокус остаётся в меню (T169): с клавиатуры — на соседнем пункте
 * (его нет — на первом; пунктов нет — на меню, а на первом пункте — когда он появится), мышью —
 * на меню.
 */
function RetryItem({ onRetry }: { onRetry: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const keepFocus = useContext(MenuFocusContext);
  const byPointer = useRef(false);
  useLayoutEffect(() => {
    const item = ref.current;
    return () => {
      // Пункт ещё в документе и с фокусом: после удаления фокус упал бы на страницу.
      if (!item?.contains(document.activeElement)) return;
      const items = Array.from(
        item.closest('[role="menu"]')?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? [],
      );
      const at = items.indexOf(item);
      keepFocus?.({
        keyboard: !byPointer.current,
        near: items[at - 1] ?? items[at + 1] ?? null,
      });
    };
  }, [keepFocus]);
  return (
    <Menu.Item
      ref={ref}
      className="t-mi"
      onPointerDown={() => {
        byPointer.current = true;
      }}
      onKeyDown={() => {
        byPointer.current = false;
      }}
      onSelect={(event) => {
        event.preventDefault();
        onRetry();
      }}
    >
      <Icon icon={RotateCcw} className="t-ico" />
      Повторить
    </Menu.Item>
  );
}

export function DropdownSeparator() {
  return <Menu.Separator className="t-msep" />;
}

/** Заголовок группы стилем eyebrow. */
export function DropdownLabel({ children }: { children: ReactNode }) {
  return <Menu.Label className="t-mh">{children}</Menu.Label>;
}

export function DropdownGroup({ children }: { children: ReactNode }) {
  return <Menu.Group>{children}</Menu.Group>;
}

export interface DropdownRadioGroupProps<V extends string> {
  value: V;
  onValueChange: (value: V) => void;
  children: ReactNode;
}

/** Выбор одного значения: текущее отмечено галочкой справа (menuitemradio, aria-checked). */
export function DropdownRadioGroup<V extends string>({
  value,
  onValueChange,
  children,
}: DropdownRadioGroupProps<V>) {
  return (
    <Menu.RadioGroup value={value} onValueChange={(next) => onValueChange(next as V)}>
      {children}
    </Menu.RadioGroup>
  );
}

export interface DropdownRadioItemProps {
  value: string;
  children: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
  /** Строка-пояснение под названием: «Контролёр — справочники, очереди…» (ux.md, раздел 12). */
  description?: ReactNode;
}

export function DropdownRadioItem({
  value,
  children,
  icon,
  disabled,
  description,
}: DropdownRadioItemProps) {
  const id = useId();
  const withDescription = description != null && description !== '';
  return (
    <Menu.RadioItem
      value={value}
      disabled={disabled}
      className={cx('t-mi', withDescription && 'kp-mi-2')}
      aria-labelledby={withDescription ? `${id}-t` : undefined}
      aria-describedby={withDescription ? `${id}-d` : undefined}
    >
      {icon ? <Icon icon={icon} className="t-ico" /> : null}
      {withDescription ? (
        <span className="kp-mi-text">
          <span id={`${id}-t`}>{children}</span>
          <span id={`${id}-d`} className="kp-mi-desc">
            {description}
          </span>
        </span>
      ) : (
        children
      )}
      <Menu.ItemIndicator className="ck">
        <Icon icon={Check} />
      </Menu.ItemIndicator>
    </Menu.RadioItem>
  );
}

export interface DropdownCheckboxItemProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
}

/** Выбор нескольких значений: меню остаётся открытым (ux.md, раздел 5). */
export function DropdownCheckboxItem({
  checked,
  onCheckedChange,
  children,
  disabled,
}: DropdownCheckboxItemProps) {
  return (
    <Menu.CheckboxItem
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onCheckedChange(next === true)}
      onSelect={(event) => event.preventDefault()}
      className="t-mi"
    >
      {children}
      <Menu.ItemIndicator className="ck">
        <Icon icon={Check} />
      </Menu.ItemIndicator>
    </Menu.CheckboxItem>
  );
}
